import { useState, useEffect, useCallback } from "react";
import api from "../services/api";

const MONTHS_FR = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const SOURCES = [
  { id:"bank",    icon:"🏦", label:"Relevés bancaires",         sub:"Import OFX/QIF/CSV (BNP, SG, Qonto, Shine…)", done:false },
  { id:"inv",     icon:"📄", label:"Factures clients",          sub:"Déjà dans l'app ou import CSV", done:false },
  { id:"purch",   icon:"🛒", label:"Factures fournisseurs",     sub:"Import CSV", done:false },
  { id:"sub",     icon:"🔄", label:"Abonnements récurrents",    sub:"Génération automatique depuis le module Abonnements", done:false },
  { id:"ndf",     icon:"🧾", label:"Notes de frais",            sub:"Saisie ou import CSV", done:false },
  { id:"payroll", icon:"👥", label:"Salaires",                  sub:"Import depuis DSN ou saisie manuelle", done:false },
  { id:"tva",     icon:"📊", label:"Déclarations TVA",          sub:"Récupération automatique ou saisie", done:false },
];

const BANK_FORMATS = [
  { key:"ofx",    label:"OFX (standard bancaire)" },
  { key:"qif",    label:"QIF (Quicken)" },
  { key:"csv",    label:"CSV générique" },
  { key:"bnp",    label:"BNP Paribas" },
  { key:"sg",     label:"Société Générale" },
  { key:"cic",    label:"CIC / Crédit Mutuel" },
  { key:"ca",     label:"Crédit Agricole" },
  { key:"qonto",  label:"Qonto" },
  { key:"shine",  label:"Shine" },
  { key:"bourso", label:"Boursorama" },
];

function fmt(n) { return (n||0).toLocaleString("fr-FR", { maximumFractionDigits:0 }); }
function fmtD(d) { return d ? new Date(d).toLocaleDateString("fr-FR") : "—"; }

export default function Rattrapage({ currentCompanyId, currentCompany, onNavigate }) {
  const currentYear = new Date().getFullYear();
  const [step, setStep]             = useState(1);
  const [year, setYear]             = useState(String(currentYear));
  const [session, setSession]       = useState(null);
  const [months, setMonths]         = useState([]);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(false);

  // Étape 1 : paramètres session
  const [periodStart, setPeriodStart]  = useState(`${currentYear}-01-01`);
  const [periodEnd, setPeriodEnd]      = useState(new Date().toISOString().slice(0,10));
  const [openingBalance, setOB]        = useState("");
  const [sources, setSources]          = useState(SOURCES.map(s => ({...s, done: false})));

  // Étape 3 : import relevés
  const [bankFile, setBankFile]        = useState(null);
  const [bankContent, setBankContent]  = useState(null);
  const [bankAnalysis, setBankAnalysis]= useState(null);
  const [bankAnalyzing, setBA]         = useState(false);
  const [bankImporting, setBI]         = useState(false);
  const [bankImportResult, setBIR]     = useState(null);
  const [bankImports, setBankImports]  = useState([]);
  const [bankError, setBankError]      = useState("");

  // Étape 4 : staging
  const [staged, setStaged]            = useState([]);
  const [stagingLoading, setSL]        = useState(false);

  // Étape 5 : validation mensuelle
  const [selectedMonth, setSelMonth]   = useState(null);
  const [monthTva, setMonthTva]        = useState(null);
  const [validating, setValidating]    = useState(false);

  const loadSession = useCallback(() => {
    if (!currentCompanyId || !year) return;
    setLoading(true);
    api.catchup.session(currentCompanyId, year)
      .then(d => {
        if (d) { setSession(d.session); setMonths(d.months||[]); setStats(d.stats); }
        else { setSession(null); setMonths([]); setStats(null); }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId, year]);

  useEffect(() => { loadSession(); }, [loadSession]);

  const loadBankImports = useCallback(() => {
    if (!currentCompanyId) return;
    api.bankImport.imports(currentCompanyId).then(setBankImports).catch(() => {});
  }, [currentCompanyId]);

  useEffect(() => { loadBankImports(); }, [loadBankImports]);

  const startSession = async () => {
    if (!currentCompanyId) return;
    try {
      await api.catchup.createSession(currentCompanyId, {
        year, period_start: periodStart, period_end: periodEnd,
        opening_bank_balance: parseFloat(openingBalance)||0,
      });
      loadSession();
      setStep(2);
    } catch(e) { alert(e.message); }
  };

  const loadBankFile = (f) => {
    setBankFile(f); setBankAnalysis(null); setBankError("");
    // Toujours en binaire (base64) : le serveur reconnaît le format par le
    // contenu (Excel .xlsx/.xls, OFX, QIF, CSV) et détecte l'encodage.
    const r = new FileReader();
    r.onload = e => setBankContent(String(e.target.result).split(",")[1] || "");
    r.readAsDataURL(f);
  };

  const analyzeBankFile = async () => {
    if (!bankContent || !currentCompanyId) return;
    setBA(true);
    try {
      const res = await api.bankImport.analyze(currentCompanyId, { content: bankContent, filename: bankFile?.name, encoding: "base64" });
      setBankAnalysis(res);
    } catch(e) { setBankError(e.message); }
    finally { setBA(false); }
  };

  const importBankFile = async () => {
    if (!bankContent || !currentCompanyId) return;
    setBI(true); setBankError("");
    try {
      const res = await api.bankImport.import(currentCompanyId, { content: bankContent, filename: bankFile?.name, encoding: "base64" });
      setBIR(res); setBankFile(null); setBankContent(null); setBankAnalysis(null);
      loadBankImports();
    } catch(e) { setBankError(e.message); }
    finally { setBI(false); }
  };

  const loadStaged = async () => {
    if (!currentCompanyId) return;
    setSL(true);
    try { setStaged(await api.bankImport.staging(currentCompanyId, { status: "pending" })); }
    catch(e) { console.error(e); }
    finally { setSL(false); }
  };

  const loadMonthTva = async (m) => {
    if (!currentCompanyId) return;
    try { setMonthTva(await api.catchup.monthTva(currentCompanyId, year, m)); }
    catch(e) { console.error(e); }
  };

  const validateMonth = async (m, sessionId) => {
    if (!sessionId) return;
    setValidating(true);
    try {
      const tva = await api.catchup.monthTva(currentCompanyId, year, m);
      await api.catchup.validateMonth(sessionId, m, {
        status: "validated",
        tva_collectee:  tva.tva_collectee,
        tva_deductible: tva.tva_deductible,
      });
      loadSession();
    } catch(e) { alert(e.message); }
    finally { setValidating(false); }
  };

  const validateAllStaged = async (importId) => {
    try { await api.bankImport.validate(importId); loadBankImports(); loadStaged(); }
    catch(e) { alert(e.message); }
  };

  const STEPS = [
    { n:1, label:"Paramètres" },
    { n:2, label:"Sources" },
    { n:3, label:"Import bancaire" },
    { n:4, label:"Réconciliation" },
    { n:5, label:"Validation" },
  ];

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Rattrapage comptable</h2>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <label style={{ fontSize:13 }}>Exercice :</label>
          <select className="form-control" value={year} onChange={e => setYear(e.target.value)} style={{ width:100 }}>
            {[0,1,2,3].map(i => { const y = currentYear-i; return <option key={y} value={y}>{y}</option>; })}
          </select>
        </div>
      </div>

      {/* Stepper */}
      <div style={{ display:"flex", gap:0, marginBottom:24, overflowX:"auto" }}>
        {STEPS.map((s,i) => (
          <div key={s.n} style={{ display:"flex", alignItems:"center" }}>
            <div onClick={() => session && setStep(s.n)} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", borderRadius:8, cursor: session || s.n===1 ? "pointer":"default",
              background: step===s.n ? "var(--g)" : "transparent",
              color:      step===s.n ? "#fff" : "var(--text-muted)",
            }}>
              <div style={{ width:22, height:22, borderRadius:"50%",
                background: step===s.n ? "rgba(255,255,255,0.3)" : step>s.n ? "var(--g)" : "#e5e7eb",
                display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700,
                color: step>s.n ? "#fff" : step===s.n ? "#fff" : "#6b7280",
              }}>{step>s.n ? "✓" : s.n}</div>
              <span style={{ fontSize:13, fontWeight: step===s.n?700:400, whiteSpace:"nowrap" }}>{s.label}</span>
            </div>
            {i < STEPS.length-1 && <div style={{ width:24, height:1, background:"var(--border)" }} />}
          </div>
        ))}
      </div>

      {/* ── ÉTAPE 1 : Paramètres ─────────────────────────────────── */}
      {step === 1 && (
        <div className="g2">
          <div className="card">
            <div className="card-header"><span className="card-title">📅 Définir la période de rattrapage</span></div>
            <div className="form-group">
              <label className="form-label">Exercice fiscal</label>
              <input className="form-control" value={year} readOnly style={{ background:"#f3f4f6" }} />
            </div>
            <div className="g2">
              <div className="form-group">
                <label className="form-label">Début de la période</label>
                <input className="form-control" type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Fin de la période</label>
                <input className="form-control" type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Solde bancaire au 01/01/{year} (depuis relevé)</label>
              <input className="form-control" type="number" step="0.01" value={openingBalance} onChange={e => setOB(e.target.value)} placeholder="ex: 12500.00" />
            </div>
            {session ? (
              <div style={{ padding:"10px 14px", background:"#d1fae5", borderRadius:8, fontSize:13, marginBottom:8 }}>
                ✅ Session {year} déjà créée — {fmtD(session.period_start)} → {fmtD(session.period_end)}
              </div>
            ) : null}
            <button className="btn primary" style={{ width:"100%" }} onClick={session ? () => setStep(2) : startSession}>
              {session ? "Continuer →" : `Démarrer le rattrapage ${year}`}
            </button>
          </div>

          {session && (
            <div className="card">
              <div className="card-header"><span className="card-title">📈 Progression actuelle</span></div>
              <div style={{ fontSize:36, fontWeight:900, color:"var(--g)", marginBottom:8 }}>{Math.round(session.completeness_score||0)}%</div>
              <div style={{ height:8, background:"#f3f4f6", borderRadius:4, marginBottom:16, overflow:"hidden" }}>
                <div style={{ width:`${session.completeness_score||0}%`, height:"100%", background:"var(--g)", borderRadius:4 }} />
              </div>
              {stats && (
                <div style={{ display:"flex", flexDirection:"column", gap:6, fontSize:13 }}>
                  <div>📝 <strong>{fmt(stats.transactions)}</strong> écritures comptables</div>
                  <div>📄 <strong>{fmt(stats.invoices)}</strong> factures</div>
                  <div>🏦 <strong>{stats.bank_imports}</strong> import(s) de relevés · {fmt(stats.bank_lines)} lignes</div>
                  <div style={{ color: stats.staged_validated > 0 ? "var(--g)" : "var(--text-muted)" }}>
                    ✓ <strong>{fmt(stats.staged_validated)}/{fmt(stats.staged_total)}</strong> transactions validées
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── ÉTAPE 2 : Sources ─────────────────────────────────────── */}
      {step === 2 && (
        <div className="card">
          <div className="card-header"><span className="card-title">📋 Sources de données disponibles</span></div>
          <p style={{ fontSize:13, color:"var(--text-muted)", marginBottom:16 }}>
            Cochez les sources que vous allez connecter ou importer pour le rattrapage {year}.
          </p>
          <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:20 }}>
            {sources.map((s,i) => (
              <div key={s.id} onClick={() => setSources(prev => prev.map((x,j) => j===i ? {...x, done:!x.done} : x))}
                style={{ display:"flex", alignItems:"center", gap:14, padding:"12px 14px", borderRadius:8, cursor:"pointer",
                  background: s.done ? "rgba(26,158,114,0.05)" : "#f9fafb",
                  border: `1px solid ${s.done ? "var(--g)" : "var(--border)"}`,
                }}>
                <div style={{ width:20, height:20, borderRadius:4, border:`2px solid ${s.done?"var(--g)":"#ccc"}`,
                  background: s.done?"var(--g)":"transparent", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {s.done && <span style={{ color:"#fff", fontSize:12 }}>✓</span>}
                </div>
                <div style={{ fontSize:20 }}>{s.icon}</div>
                <div>
                  <div style={{ fontWeight:600, fontSize:13 }}>{s.label}</div>
                  <div style={{ fontSize:12, color:"var(--text-muted)" }}>{s.sub}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button className="btn" onClick={() => setStep(1)}>← Retour</button>
            <button className="btn primary" onClick={() => setStep(3)}>Importer les relevés →</button>
          </div>
        </div>
      )}

      {/* ── ÉTAPE 3 : Import bancaire ─────────────────────────────── */}
      {step === 3 && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {/* Zone drop */}
          {!bankFile && !bankImportResult && (
            <div className="card">
              <div className="card-header"><span className="card-title">🏦 Import relevés bancaires</span></div>
              <p style={{ fontSize:13, color:"var(--text-muted)", marginBottom:12 }}>
                Formats supportés : <strong>OFX · QIF · CSV</strong> — BNP, SG, CIC, Crédit Agricole, Qonto, Shine, Boursorama…
              </p>
              <div onClick={() => document.getElementById("bank-file-input").click()}
                style={{ border:"2px dashed var(--border)", borderRadius:12, padding:36, textAlign:"center", cursor:"pointer",
                  background:"#fafafa", transition:"border-color 0.2s" }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const f=e.dataTransfer?.files?.[0]; if(f) loadBankFile(f); }}
                onMouseEnter={e => e.currentTarget.style.borderColor="var(--g)"}
                onMouseLeave={e => e.currentTarget.style.borderColor="var(--border)"}
              >
                <div style={{ fontSize:32, marginBottom:10 }}>🏦</div>
                <div style={{ fontWeight:700, marginBottom:4 }}>Glisser-déposer votre relevé bancaire</div>
                <div style={{ fontSize:12, color:"var(--text-muted)" }}>
                  {BANK_FORMATS.map(f => f.label).join(" · ")}
                </div>
                <input id="bank-file-input" type="file" accept=".xlsx,.xls,.ofx,.qif,.csv,.txt" onChange={e => loadBankFile(e.target.files[0])} style={{ display:"none" }} />
              </div>
            </div>
          )}

          {/* Fichier chargé */}
          {bankFile && !bankImportResult && (
            <>
              <div className="card">
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontSize:24 }}>📄</span>
                    <div>
                      <div style={{ fontWeight:700 }}>{bankFile.name}</div>
                      <div style={{ fontSize:12, color:"var(--text-muted)" }}>{(bankFile.size/1024).toFixed(1)} Ko</div>
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:8 }}>
                    <button className="btn" onClick={() => { setBankFile(null); setBankContent(null); setBankAnalysis(null); setBankError(""); }}>✕</button>
                    <button className="btn primary" onClick={analyzeBankFile} disabled={bankAnalyzing}>
                      {bankAnalyzing ? "Analyse…" : "🔍 Analyser"}
                    </button>
                  </div>
                </div>
              </div>

              {bankAnalysis && (
                <div className="card">
                  <div className="card-header"><span className="card-title">📊 Rapport d'analyse</span></div>
                  <div className="kpi-grid k4" style={{ marginBottom:12 }}>
                    <div className="kpi"><div className="kpi-label">Format détecté</div><div className="kpi-value" style={{ fontSize:14 }}>{bankAnalysis.format?.toUpperCase()}</div></div>
                    <div className="kpi"><div className="kpi-label">Transactions</div><div className="kpi-value">{fmt(bankAnalysis.count)}</div></div>
                    <div className="kpi"><div className="kpi-label">Entrées</div><div className="kpi-value" style={{ color:"var(--g)" }}>+{fmt(bankAnalysis.total_in)} €</div></div>
                    <div className="kpi"><div className="kpi-label">Sorties</div><div className="kpi-value" style={{ color:"var(--red)" }}>-{fmt(bankAnalysis.total_out)} €</div></div>
                  </div>
                  <div style={{ fontSize:12, color:"var(--text-muted)", marginBottom:12 }}>
                    Période : {fmtD(bankAnalysis.period?.from)} → {fmtD(bankAnalysis.period?.to)}
                  </div>
                  {bankAnalysis.categories && (
                    <div style={{ fontSize:12, display:"flex", flexWrap:"wrap", gap:"4px 12px" }}>
                      {Object.entries(bankAnalysis.categories).slice(0,8).map(([k,v]) => (
                        <span key={k} style={{ color:"var(--text-muted)" }}>{k} ({v})</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {bankError && <div style={{ padding:"10px 14px", background:"#dc262610", border:"1px solid #dc262630", borderRadius:8, color:"#dc2626", fontSize:13 }}>{bankError}</div>}

              <button className="btn primary" style={{ width:"100%" }} onClick={importBankFile} disabled={bankImporting || !bankAnalysis}>
                {bankImporting ? "Import en cours…" : "📥 Importer en staging (à valider)"}
              </button>
            </>
          )}

          {/* Résultat import */}
          {bankImportResult && (
            <div className="card">
              <div style={{ textAlign:"center", padding:"12px 0" }}>
                <div style={{ fontSize:36, marginBottom:8 }}>✅</div>
                <div style={{ fontWeight:700, marginBottom:4 }}>Import terminé</div>
                <div style={{ fontSize:13, color:"var(--text-muted)", marginBottom:12 }}>{bankImportResult.total} transactions importées</div>
              </div>
              <div className="kpi-grid k4" style={{ marginBottom:12 }}>
                <div className="kpi"><div className="kpi-label">Total</div><div className="kpi-value">{fmt(bankImportResult.total)}</div></div>
                <div className="kpi"><div className="kpi-label">Rapprochées auto</div><div className="kpi-value" style={{ color:"var(--g)" }}>{fmt(bankImportResult.reconciled)}</div></div>
                <div className="kpi"><div className="kpi-label">À valider</div><div className="kpi-value" style={{ color:"var(--amber)" }}>{fmt(bankImportResult.pending)}</div></div>
                <div className="kpi"><div className="kpi-label">Taux</div><div className="kpi-value">{bankImportResult.reconciliation_rate}%</div></div>
              </div>
              <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
                <button className="btn" onClick={() => { setBIR(null); setBankError(""); }}>Importer un autre</button>
                <button className="btn primary" onClick={() => { loadStaged(); setStep(4); }}>Réconcilier →</button>
              </div>
            </div>
          )}

          {/* Historique imports */}
          {bankImports.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">📋 Relevés importés</span></div>
              <table className="tbl">
                <thead><tr><th>Fichier</th><th>Format</th><th>Période</th><th>Total</th><th>Validés</th><th>En attente</th><th></th></tr></thead>
                <tbody>
                  {bankImports.map(imp => (
                    <tr key={imp.id}>
                      <td style={{ fontFamily:"monospace", fontSize:11 }}>{imp.filename||"—"}</td>
                      <td style={{ textTransform:"uppercase", fontSize:11 }}>{imp.format}</td>
                      <td style={{ fontSize:12 }}>{fmtD(imp.period_from)} → {fmtD(imp.period_to)}</td>
                      <td>{fmt(imp.total_lines)}</td>
                      <td style={{ color:"var(--g)" }}>{fmt(imp.validated_count||0)}</td>
                      <td style={{ color: imp.pending_count>0?"var(--amber)":"var(--text-muted)" }}>{fmt(imp.pending_count)}</td>
                      <td>
                        {imp.pending_count > 0 && (
                          <button className="btn btn-sm primary" style={{ fontSize:11, padding:"2px 8px" }}
                            onClick={() => validateAllStaged(imp.id)}>Valider tout</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button className="btn" onClick={() => setStep(2)}>← Retour</button>
            <button className="btn primary" onClick={() => { loadStaged(); setStep(4); }}>Réconciliation →</button>
          </div>
        </div>
      )}

      {/* ── ÉTAPE 4 : Réconciliation ──────────────────────────────── */}
      {step === 4 && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ fontSize:13, color:"var(--text-muted)" }}>
              {staged.length} transaction(s) en attente de validation
            </div>
            <button className="btn" onClick={loadStaged} disabled={stagingLoading}>↻ Actualiser</button>
          </div>

          {stagingLoading && <div style={{ textAlign:"center", padding:30, color:"var(--text-muted)" }}>Chargement…</div>}

          {!stagingLoading && staged.length === 0 && (
            <div className="card" style={{ textAlign:"center", padding:40 }}>
              <div style={{ fontSize:32, marginBottom:8 }}>✅</div>
              <div style={{ fontWeight:700, marginBottom:4 }}>Toutes les transactions sont validées</div>
              <div style={{ color:"var(--text-muted)", fontSize:13 }}>Vous pouvez passer à la validation mensuelle.</div>
            </div>
          )}

          {staged.length > 0 && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">🔗 Transactions à valider ({staged.length})</span>
                <div style={{ fontSize:12, color:"var(--text-muted)" }}>
                  {staged.filter(s => s.reconcile_score >= 85).length} rapprochées automatiquement
                </div>
              </div>
              <div className="tbl-overflow">
                <table className="tbl">
                  <thead><tr><th>Date</th><th>Libellé</th><th>Montant</th><th>Compte PCG</th><th>Rapprochement</th><th></th></tr></thead>
                  <tbody>
                    {staged.slice(0,100).map(t => (
                      <tr key={t.id}>
                        <td style={{ fontSize:12, whiteSpace:"nowrap" }}>{fmtD(t.date)}</td>
                        <td style={{ fontSize:12, maxWidth:220 }}><div style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.label}</div></td>
                        <td style={{ fontWeight:700, color:t.amount<0?"var(--red)":"var(--g)", whiteSpace:"nowrap" }}>
                          {t.amount > 0 ? "+" : ""}{fmt(t.amount)} €
                        </td>
                        <td style={{ fontSize:11 }}>
                          {t.category_account ? <span style={{ fontFamily:"monospace", background:"#f3f4f6", padding:"1px 5px", borderRadius:4 }}>{t.category_account}</span> : <span style={{ color:"var(--text-muted)" }}>—</span>}
                        </td>
                        <td style={{ fontSize:11 }}>
                          {t.invoice_number ? (
                            <span style={{ color:"var(--g)" }}>✓ Facture {t.invoice_number}</span>
                          ) : t.reconcile_score > 0 ? (
                            <span style={{ color:"var(--amber)" }}>~{t.reconcile_score}%</span>
                          ) : <span style={{ color:"var(--text-muted)" }}>—</span>}
                        </td>
                        <td>
                          <button className="btn btn-sm" style={{ fontSize:11, padding:"2px 8px", color:"var(--g)", border:"1px solid var(--g)" }}
                            onClick={() => api.bankImport.updateStaging(t.id, { status:"validated" }).then(loadStaged)}>
                            ✓
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {staged.length > 100 && <div style={{ textAlign:"center", fontSize:12, color:"var(--text-muted)", marginTop:8 }}>…et {staged.length-100} autres transactions</div>}
            </div>
          )}

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button className="btn" onClick={() => setStep(3)}>← Retour</button>
            <button className="btn primary" onClick={() => setStep(5)}>Validation mensuelle →</button>
          </div>
        </div>
      )}

      {/* ── ÉTAPE 5 : Validation mensuelle ───────────────────────── */}
      {step === 5 && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">📅 Validation mois par mois — {year}</span></div>
            <p style={{ fontSize:13, color:"var(--text-muted)", marginBottom:16 }}>
              Validez chaque mois après vérification du solde bancaire et du calcul TVA.
            </p>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(150px, 1fr))", gap:8 }}>
              {months.map(m => {
                const today = new Date();
                const isPast = new Date(`${m.year}-${String(m.month).padStart(2,'0')}-01`) <= today;
                return (
                  <div key={m.id} onClick={() => { setSelMonth(m); loadMonthTva(m.month); }}
                    style={{ padding:"12px 14px", borderRadius:8, cursor:"pointer",
                      border:`1.5px solid ${m.status==="validated" ? "var(--g)" : selectedMonth?.id===m.id ? "var(--g)" : "var(--border)"}`,
                      background: m.status==="validated" ? "rgba(26,158,114,0.07)" : selectedMonth?.id===m.id ? "rgba(26,158,114,0.03)" : "#fff",
                    }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                      <div style={{ fontWeight:700, fontSize:13 }}>{MONTHS_FR[m.month-1]}</div>
                      <span style={{ fontSize:16 }}>
                        {m.status==="validated" ? "✅" : isPast ? "⏳" : "—"}
                      </span>
                    </div>
                    {m.tva_collectee > 0 && <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:4 }}>TVA : {fmt(m.tva_collectee-m.tva_deductible)} €</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {selectedMonth && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">📊 {MONTHS_FR[selectedMonth.month-1]} {year}</span>
              </div>
              <div className="g2">
                <div>
                  <div style={{ marginBottom:12 }}>
                    <label className="form-label">Solde bancaire fin de mois</label>
                    <input className="form-control" type="number" step="0.01"
                      defaultValue={selectedMonth.bank_balance_end||""} placeholder="ex: 15000.00"
                      onChange={e => setSelMonth(m => ({...m, bank_balance_end: parseFloat(e.target.value)||0}))} />
                  </div>
                </div>
                {monthTva && (
                  <div style={{ padding:"12px 14px", background:"#f8f9fa", borderRadius:8 }}>
                    <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>TVA calculée automatiquement</div>
                    <div style={{ fontSize:13, display:"flex", flexDirection:"column", gap:4 }}>
                      <div>Collectée : <strong style={{ color:"var(--red)" }}>{fmt(monthTva.tva_collectee)} €</strong></div>
                      <div>Déductible : <strong style={{ color:"var(--g)" }}>{fmt(monthTva.tva_deductible)} €</strong></div>
                      <div style={{ borderTop:"var(--border)", paddingTop:6, marginTop:2 }}>
                        {monthTva.to_pay > 0
                          ? <><strong>À payer : {fmt(monthTva.to_pay)} €</strong></>
                          : <><strong style={{ color:"var(--g)" }}>Crédit : {fmt(monthTva.credit)} €</strong></>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <button className="btn primary" style={{ marginTop:12 }} onClick={() => validateMonth(selectedMonth.month, session?.id)} disabled={validating || selectedMonth.status==="validated"}>
                {selectedMonth.status==="validated" ? "✓ Mois validé" : validating ? "Validation…" : `✓ Valider ${MONTHS_FR[selectedMonth.month-1]}`}
              </button>
            </div>
          )}

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button className="btn" onClick={() => setStep(4)}>← Retour</button>
            <button className="btn primary" onClick={() => onNavigate?.("fec")}>Exporter le FEC →</button>
          </div>
        </div>
      )}
    </div>
  );
}
